import { useState } from "react";
import { toast } from "sonner";
import { AccountType, dayjs } from "@boo/shared";
import type { AccountItem } from "@boo/shared";
import { Button } from "@/components/ui/button";
import AmountInput from "@/components/AmountInput";
import { ApiError } from "@/services/expenseApi";
import { useAccountStore } from "../store/useAccountStore";
import { INPUT_CLASS } from "../types/accounts.types";

interface Props {
  account: AccountItem;
  onDone: () => void;
}

export default function CheckpointForm({ account, onDone }: Props) {
  const addCheckpoint = useAccountStore((s) => s.addCheckpoint);
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [balance, setBalance] = useState("");
  const [saving, setSaving] = useState(false);

  const owed = account.type === AccountType.CreditCard;
  const balanceNum = parseFloat(balance);
  const valid = !isNaN(balanceNum);

  async function handleSubmit(e: { preventDefault(): void }) {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    try {
      await addCheckpoint(account.id, { date, balance: balanceNum });
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 flex flex-wrap items-end gap-2 border-t border-border pt-3"
    >
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        Date
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className={`${INPUT_CLASS} [font-variant-numeric:tabular-nums]`}
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        {owed ? "Amount owed (฿)" : "Balance (฿)"}
        <AmountInput
          className={`${INPUT_CLASS} [font-variant-numeric:tabular-nums]`}
          allowNegative
          value={balance}
          onChange={setBalance}
        />
      </label>
      <Button type="submit" size="sm" disabled={!valid} loading={saving}>
        Save
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={onDone}>
        Cancel
      </Button>
    </form>
  );
}
